/**
 * Which language this visitor reads the interface in.
 *
 * Three sources, in the order the visitor would expect them to win:
 *
 * 1. **their account**, when they are signed in — `useProfileSync` restores the
 *    account's preferences on top of the device, so by the time this module is
 *    read the account's choice is already in the preference store;
 * 2. **this device**, for an anonymous visitor — the same store, persisted to
 *    `localStorage`;
 * 3. **the browser**, for a visitor who has never said — `navigator.languages`
 *    is the ordered preference list the browser also sends as `Accept-Language`.
 *
 * The first two are one lookup because they are one store: `Settings`
 * already splits device from account exactly this way, and `profileSnapshot.ts`
 * argues at length for the merge rule that makes a fresh sign-in behave. A
 * second store beside it would have had to re-derive all of it.
 *
 * The page is server-rendered, and the server resolves the same three sources
 * from the two places it can see them: the locale cookie this module mirrors
 * the choice into, and the `Accept-Language` header the browser derives from
 * the same list read here. `providers/initialLocale.ts` is that side of it, and
 * the locale it resolves is what the first client render uses — this module
 * takes over from the first change after that.
 */

import {
  directionOf,
  FALLBACK_LOCALE,
  isCoveredLocale,
  resolveLocale,
  type TextDirection,
} from "../support/locales";
import { localeCookieAssignment } from "../support/localeCookie";
import {
  getSettings,
  setSetting,
  subscribe as subscribePreferences,
} from "./settings";

/** What the browser asks for, most-preferred first. Empty off the browser. */
export function requestedLocales(): readonly string[] {
  if (typeof navigator === "undefined") return [];
  const languages = navigator.languages;
  if (Array.isArray(languages) && languages.length > 0) return languages;
  return navigator.language ? [navigator.language] : [];
}

/**
 * The locale in force: the visitor's choice when they have made one, and what
 * their browser asks for when they have not.
 */
export function getLocale(): string {
  const chosen = getSettings().locale;
  if (chosen && isCoveredLocale(chosen)) return chosen;
  return resolveLocale(requestedLocales());
}

/** The locale this visitor picked, or `""` while they are following the browser. */
export function getChosenLocale(): string {
  const chosen = getSettings().locale;
  return chosen && isCoveredLocale(chosen) ? chosen : "";
}

/**
 * Record a choice. `""` gives the decision back to the browser, which is what
 * "System" means for the theme and means the same here.
 */
export function setLocale(code: string): void {
  if (code !== "" && !isCoveredLocale(code)) return;
  setSetting("locale", code);
}

/** The writing direction of the locale in force. */
export function getDirection(): TextDirection {
  return directionOf(getLocale());
}

/**
 * Mirror the choice into the cookie the server reads.
 *
 * Written here rather than in `setLocale` because a choice also arrives from
 * the account: signing in restores the account's preferences over the device's,
 * and that path never passes through `setLocale`. Everything that changes the
 * choice notifies, so the seam that follows the store calls this and there is
 * one write site instead of two.
 */
export function syncLocaleCookie(): void {
  if (typeof document === "undefined") return;
  document.cookie = localeCookieAssignment(
    getChosenLocale(),
    typeof location !== "undefined" && location.protocol === "https:",
  );
}

/**
 * Notify on any change to the locale in force or to the choice behind it. It is
 * the preference store's subscription filtered down to this one key, so a
 * slider drag does not wake the i18next seam.
 *
 * The choice is watched as well as the locale because the two can move
 * independently: a visitor whose browser asks for Russian and who then picks
 * Russian explicitly changes nothing on the screen and everything about what
 * the server should be told.
 */
export function subscribe(listener: () => void): () => void {
  const state = () => `${getLocale()}\u0000${getChosenLocale()}`;
  let last = state();
  return subscribePreferences(() => {
    const next = state();
    if (next === last) return;
    last = next;
    listener();
  });
}

/**
 * What the document should declare, computed together because they change
 * together and `<html>` carries both.
 */
export function documentLocale(): { lang: string; dir: TextDirection } {
  const lang = getLocale();
  return { lang, dir: directionOf(lang) };
}

export { FALLBACK_LOCALE };
